import { useState, useEffect, useMemo } from "react";
import { SectionShell, EmptyState } from "./common";
import { FormDialog } from "./FormDialog";
import { PostCard } from "./PostCard";
import { TopPosts } from "./TopPosts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePagedCollection } from "@/hooks/usePagedCollection";
import { api } from "@/lib/api";
import { MessageSquarePlus, MessagesSquare, Search, X, Loader2 } from "lucide-react";
import { analytics } from "@/lib/analytics";
import { REGIONS, REGION_ORDER } from "@/lib/ncRegions";
import { KolamAvatar } from "./KolamAvatar";
import { addPendingPost, reconcilePending } from "@/lib/pendingPosts";
import { Clock } from "lucide-react";

function PendingStrip({ posts }) {
  if (posts.length === 0) return null;
  return (
    <div className="mb-8 rounded-2xl border border-[#0071e3]/25 bg-[#0071e3]/[0.04] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-3.5 h-3.5 text-[#0071e3]" />
        <p className="text-[13px] font-medium text-foreground">
          {posts.length === 1 ? "Your post is in review" : `${posts.length} of your posts are in review`}
        </p>
      </div>
      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="flex items-start gap-3">
            <KolamAvatar name={p.name} city={p.city} size={28} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[13px] font-medium text-foreground truncate">{p.title}</p>
                <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0071e3]/10 text-[#0071e3] shrink-0">
                  In review
                </span>
              </div>
              <p className="text-[12px] text-muted-foreground line-clamp-2 mt-0.5">{p.body}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[12px] text-muted-foreground mt-3 pt-3 border-t border-[#0071e3]/15">
        Every post gets a quick human check before it goes live — usually within a few hours. Only you can see this.
      </p>
    </div>
  );
}

const TOPICS = ["All","General","Advice","Moving to NC","Recommendations","Tech Chat"];

const SORTS = [
  { key: "recent",     label: "Recent" },
  { key: "popular",    label: "Popular" },
  { key: "unanswered", label: "Unanswered" },
];

const FIELDS = [
  { name:"author", label:"Your name", type:"text", placeholder:"e.g. Priya S. (or leave as Anonymous)", default:"Anonymous" },
  { name:"city", label:"City", type:"city", placeholder:"Start typing — Cary, Winston-Salem, anywhere in NC" },
  { name:"topic", label:"Topic", type:"select", options:["General","Advice","Moving to NC","Recommendations","Tech Chat"], default:"General", required:true },
  { name:"title", label:"Title", type:"text", required:true, placeholder:"Give your post a clear title" },
  { name:"content", label:"What's on your mind?", type:"textarea", required:true, rows:5, placeholder:"Ask a question, share a tip, announce something..." },
];

export function CommunityBoard() {
  const [topic, setTopic]   = useState("All");
  const [region, setRegion] = useState("all");
  const [sort, setSort]     = useState("recent");
  const [search, setSearch] = useState("");
  const [query, setQuery]   = useState("");   // debounced value actually sent
  const [top, setTop]       = useState([]);
  const [pending, setPending] = useState([]);

  // Debounce search so we don't fire a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      if (search.trim().length > 2) analytics.searchPosts(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p = { sort };
    if (topic !== "All") p.topic = topic;
    if (region !== "all") p.region = region;
    if (query) p.q = query;
    return p;
  }, [topic, region, sort, query]);

  const { items, total, hasMore, loading, loadingMore, loadMore, prepend } =
    usePagedCollection("posts", params);

  const refreshTop = () =>
    api.get("/posts/top", { params: { days: 7, limit: 3 } })
      .then(({ data }) => setTop(Array.isArray(data) ? data : []))
      .catch(() => setTop([]));

  useEffect(() => { refreshTop(); }, []);

  // Show this browser's own posts that are still awaiting approval,
  // and drop any that have since gone live.
  useEffect(() => { reconcilePending().then(setPending); }, []);

  const handleCreated = (created) => {
    const post = created?.post || created;
    addPendingPost(post);
    reconcilePending().then(setPending);
  };

  const isFiltered = topic !== "All" || region !== "all" || !!query || sort !== "recent";

  const clearAll = () => { setTopic("All"); setRegion("all"); setSort("recent"); setSearch(""); };

  return (
    <SectionShell
      id="community-board"
      overline="Community Board"
      title="What's on everyone's mind"
      subtitle="This is your open mic. Ask where to find the best chaat in Cary. Warn about a scam. Announce your Diwali party. No judgment. Just community."
      action={
        <FormDialog
          resource="posts"
          title="Share with the community"
          description="No login needed. Post freely — the community is listening."
          fields={FIELDS}
          onCreated={handleCreated}
          submitLabel="Post"
          testIdPrefix="post"
          trigger={<Button className="rounded-full px-6"><MessageSquarePlus className="w-4 h-4 mr-2" />Start a conversation</Button>}
        />
      }
    >
      {/* Your own submissions awaiting approval — visible only to you */}
      <PendingStrip posts={pending} />

      {/* Top posts only make sense on the unfiltered default view */}
      {!isFiltered && <TopPosts posts={top} />}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search posts — try 'grocery', 'schools', 'H1B'…"
          className="pl-9 pr-9 h-11 rounded-xl"
          aria-label="Search posts"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Topic chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        {TOPICS.map(t => (
          <button
            key={t}
            onClick={() => { setTopic(t); analytics.filterTopic(t); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              topic === t
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Region chips */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[12px] text-muted-foreground mr-1">Region</span>
        <button
          onClick={() => { setRegion("all"); analytics.filterRegion("all"); }}
          className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors border ${
            region === "all"
              ? "bg-foreground text-background border-foreground"
              : "bg-transparent text-muted-foreground border-border hover:text-foreground"
          }`}
        >
          All NC
        </button>
        {REGION_ORDER.map(key => (
          <button
            key={key}
            onClick={() => { setRegion(key); analytics.filterRegion(key); }}
            className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors border ${
              region === key
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-border hover:text-foreground"
            }`}
          >
            {REGIONS[key].label}
          </button>
        ))}
      </div>

      {/* Sort + result count */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-8">
        <div className="flex items-center gap-1">
          {SORTS.map(s => (
            <button
              key={s.key}
              onClick={() => { setSort(s.key); analytics.sortPosts(s.key); }}
              className={`px-3 py-1 rounded-lg text-[13px] font-medium transition-colors ${
                sort === s.key
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {!loading && (
          <p className="text-[13px] text-muted-foreground">
            {total} {total === 1 ? "post" : "posts"}
            {query && <> matching "{query}"</>}
          </p>
        )}

        {isFiltered && (
          <button
            onClick={clearAll}
            className="text-[13px] text-primary hover:underline ml-auto"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={MessagesSquare}
          message={
            query
              ? `No posts match "${query}". Try a different word, or start the conversation yourself.`
              : sort === "unanswered"
              ? "Every question here has an answer. That's a good sign."
              : "No posts here yet today. Be the first. The community is listening."
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p, i) => (
              <PostCard key={p.id} post={p} index={i} onChanged={refreshTop} />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center mt-10">
              <Button
                variant="outline"
                onClick={() => { loadMore(); analytics.loadMorePosts(); }}
                disabled={loadingMore}
                className="rounded-full px-8"
              >
                {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Load more posts
              </Button>
            </div>
          )}
        </>
      )}
    </SectionShell>
  );
}
